import React, { useRef, useState, useLayoutEffect, useEffect, cloneElement } from "react";

/**
 * Drop-in replacement for Recharts' ResponsiveContainer.
 * Recharts 3.x has a known ResizeObserver timing bug where the inner SVG
 * gets width=0 inside flex/grid layouts (visible on iOS Safari and some
 * desktop layouts). This component measures the wrapper width itself and
 * passes an explicit `width` prop to the child chart, which renders reliably.
 *
 * Usage is identical to ResponsiveContainer:
 *   <ResponsiveContainer width="100%" height={240}>
 *     <BarChart data={...}>...</BarChart>
 *   </ResponsiveContainer>
 */
export function ResponsiveContainer({ height = 240, minHeight, children, className = "" }) {
  const ref = useRef(null);
  const [w, setW] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const next = el.clientWidth;
      if (next > 0) setW(next);
    };
    update();
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(update);
      ro.observe(el);
    }
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);

  // Safari iOS sometimes needs a deferred remeasure after font/layout settles.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const t = setTimeout(() => {
      const next = el.clientWidth;
      if (next > 0 && next !== w) setW(next);
    }, 150);
    return () => clearTimeout(t);
  }, []);

  const style = { width: "100%", height, minHeight: minHeight || height };

  return (
    <div ref={ref} style={style} className={className} data-testid="auto-sizer">
      {w > 0 && children
        ? cloneElement(children, { width: w, height })
        : null}
    </div>
  );
}

export default ResponsiveContainer;
